<template>
  <layout-report-step :isDone="isDone" :title="header.title" :description="header.description" :image="header.image">
    <template slot="header">
      <partner-map
        v-if="selectedPartner && isMapShown"
        :coordinates="selectedPartner?.address?.geo"
        :colour="selectedPartner?.settings?.color"
      ></partner-map>

      <debug :debug="partners" />
    </template>

    <v-row>
      <v-text-field
        outlined
        :loading="loading"
        v-model="search"
        @keydown.enter.prevent="debounceFind"
        prepend-inner-icon="mdi-magnify"
        autofocus
        persistent-hint
        :hint="$t('confirmSearchWithEnter')"
        :label="$t('report.finder.address')"
      ></v-text-field>
    </v-row>
    <v-row>
      <v-item-group active-class="primary" v-model="selected" mandatory>
        <template v-for="(item, i) in partners">
          <v-item v-slot="{ active, toggle }" :key="i">
            <v-sheet outlined rounded :class="active ? 'primary' : ''" class="mb-2 py-n6">
              <v-card flat @click="toggle">
                <v-list three-line>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ item.companyName }}
                        <v-chip v-if="item.distance" :color="active ? 'primary' : 'grey'" x-small>
                          {{ Math.round(item.distance * 10) / 10 }} km
                        </v-chip>
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        <v-icon small>mdi-map-marker-outline</v-icon>
                        {{ item.address.street }}, {{ item.address.zip }} {{ item.address.city }}
                      </v-list-item-subtitle>
                      <v-list-item-subtitle>
                        <v-icon small>mdi-email-outline</v-icon> {{ item.settings.contact.email }}
                      </v-list-item-subtitle>
                      <v-list-item-subtitle>
                        <v-icon small>mdi-phone-outline</v-icon>{{ item.settings.contact.phone }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-icon :color="active ? 'primary' : ''">
                        {{ active ? "mdi-radiobox-marked" : "mdi-radiobox-blank" }}
                      </v-icon>
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
              </v-card>
            </v-sheet>
          </v-item>
        </template>
      </v-item-group>

      <debug :debug="partners" />
    </v-row>
  </layout-report-step>
</template>

<script lang="ts">
import PartnerMap from "@/components/form/PartnerMap.vue";
import Debug from "@/components/utility/Debug.vue";
import LayoutReportStep from "@/layouts/LayoutReportStep.vue";
import { IReportHeaderData } from "@/lib/interfaces/Report/IReportHeaderData";
import { IReportStep } from "@/lib/interfaces/Report/IViewReportStep";
import { handleError } from "@/lib/utility/handleError";
import ReportStepMixin from "@/mixins/ReportStepMixin.vue";
import { ReportScreenEnum } from "@/store/enum/partner/report-screen.enum";
import { FeatureModule } from "@/store/modules/feature.store";
import { PartnerFinderModule } from "@/store/modules/partner-link-finder.store";
import { ReportControlModule } from "@/store/modules/report-control.store";
import { ReportModule } from "@/store/modules/report.store";
import { debounce } from "debounce";
import { Component, Watch } from "vue-property-decorator";

@Component({
  components: {
    LayoutReportStep,
    PartnerMap,
    Debug
  }
})
export default class ReportPartnerFinder extends ReportStepMixin implements IReportStep {
  screen = ReportScreenEnum.PARTNER_FINDER;
  loading = false;
  search = "";

  valid = false;

  selected = "";

  @Watch("selected")
  watchSelected(item: number) {
    this.$log.debug(item, this.partners[item].companyUsername);
    ReportControlModule.setSelected(this.partners[item]);
  }

  async mounted() {
    const address = `${ReportModule.address.street} ${ReportModule.address.zip} ${ReportModule.address.city}`;

    // if address is not empty search for it.
    if (address.trim()) {
      this.search = address;

      await this.find();
    }
  }

  get header(): IReportHeaderData {
    return {
      title: "report.finder.title",
      description: "report.finder.description"
    };
  }

  get isDone() {
    return Boolean(this.selectedPartner);
  }

  get partner() {
    return ReportModule.partner;
  }

  get partnerId() {
    return this.partner.id;
  }

  get partners() {
    return PartnerFinderModule.partners;
  }

  get selectedPartner() {
    return ReportControlModule.selected;
  }

  get isMapShown() {
    return FeatureModule.isMapShown;
  }

  debounceFind = debounce(this.find, 1500);

  async find() {
    if (!this.search) {
      return;
    }

    if (!this.partnerId) {
      this.$toast.error("Select partner"); // FIXME: Translate
      return;
    }

    this.loading = true;

    try {
      const res = await PartnerFinderModule.findPartners({
        partnerId: this.partnerId,
        searchstring: this.search
      });

      if (res.length <= 0) {
        this.$toast.warning("Not found"); // FIXME: Translate
      }
    } catch (error) {
      handleError(error);
    } finally {
      this.loading = false;
    }
  }
}
</script>
